import CheckCircleIcon from "@redotech/redo-icons/check-circle_filled.svg";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import CircleIcon from "@redotech/redo-icons/circle_filled.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { memo, useState } from "react";
import type { TaskStatus } from "./status-icon";
import * as css from "./widget-task-row.module.css";

export interface WidgetTaskRowProps {
  title: string;
  description: string;
  status: TaskStatus;
  /** Disabled rendering when prerequisites aren't met. */
  disabled?: boolean;
  /** Whether this row should start expanded (typically the first
   *  actionable incomplete task). */
  defaultExpanded?: boolean;
  onClick: () => void;
}

const STATUS_ICON: Record<TaskStatus, typeof CheckCircleIcon> = {
  complete: CheckCircleIcon,
  dismissed: XCloseIcon,
  incomplete: CircleIcon,
};

const STATUS_COLOR: Record<TaskStatus, "success" | "tertiary" | "disabled"> = {
  complete: "success",
  dismissed: "disabled",
  incomplete: "tertiary",
};

export const WidgetTaskRow = memo(function WidgetTaskRow({
  title,
  description,
  status,
  disabled,
  defaultExpanded = false,
  onClick,
}: WidgetTaskRowProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const Icon = STATUS_ICON[status];
  const color = STATUS_COLOR[status];
  const isComplete = status === "complete";

  return (
    <div className={css.row}>
      <button
        aria-expanded={expanded}
        className={css.rowHeader}
        disabled={disabled}
        onClick={() => setExpanded((v) => !v)}
        type="button"
      >
        <RedoIcon color={color} Icon={Icon} />
        <span
          className={
            isComplete
              ? `${css.rowTitle} ${css.rowTitleComplete}`
              : css.rowTitle
          }
        >
          {title}
        </span>
        <ChevronDownIcon
          className={
            expanded ? `${css.chevron} ${css.chevronExpanded}` : css.chevron
          }
        />
      </button>
      {expanded ? (
        <div className={css.rowBody}>
          <p className={css.rowDescription}>{description}</p>
          {!isComplete ? (
            <RedoButton
              disabled={disabled}
              hierarchy="primary"
              onClick={onClick}
              size="sm"
              text="Open"
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
});
